import type { AxiosResponse } from 'axios';
import { $api } from '../axios/axios';
import { StatisticBodyItem } from '@innowise-group/core';
import { ReportTypeUnion } from '@modules';

const MAX_SIZE = 2147483647;

export default class StatisticService {
  static async getRequestStatistics(args: StatisticBodyItem): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/vacancy/claims`, args);
  }

  static async getOverallStatuses(args: StatisticBodyItem): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/event`, args);
  }

  static async exportOverallStatuses(args: StatisticBodyItem): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/event/export`, args, {
      responseType: 'blob',
    });
  }

  static async exportRequestsData(args: StatisticBodyItem): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/vacancy/claims/export`, args, {
      responseType: 'blob',
    });
  }

  static async exportExitsData(args: StatisticBodyItem): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/job-entry/export`, args, {
      responseType: 'blob',
    });
  }

  static async exportSummaryData(args: StatisticBodyItem): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/job-entry-grouped/export`, args, {
      responseType: 'blob',
    });
  }

  static async exportTeamWorking(args: StatisticBodyItem, tableType: ReportTypeUnion): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/team/export`, args, {
      responseType: 'blob',
      params: { tableType },
    });
  }

  static async getStatusesByVacancies(args: StatisticBodyItem, page?: number): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/event/vacancy`, args, {
      params: { page, size: page ? 100 : MAX_SIZE },
    });
  }

  static async getOverallTeamStatistic(args: StatisticBodyItem): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/team`, args);
  }

  static async getVerticalTeamStatisticByVacancy(args: StatisticBodyItem, page?: number): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/team/vacancy/vertical`, args, {
      params: { page, size: page ? 100 : MAX_SIZE },
    });
  }

  static async getHorizontalTeamStatisticByVacancy(args: StatisticBodyItem, page?: number): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/team/vacancy/horizontal`, args, {
      params: { page, size: page ? 100 : MAX_SIZE },
    });
  }

  static async getPersonalStatistic(args: StatisticBodyItem, page?: number): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/event/vacancy/personal`, args, {
      params: { page, size: page ? 100 : MAX_SIZE },
    });
  }

  static async getPersonalOverallStatistic(args: StatisticBodyItem): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/event/vacancy/overall`, args);
  }

  static async getSummaryStatistic(args: StatisticBodyItem): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/job-entry-grouped`, args);
  }

  static async getExitsStatistic(args: StatisticBodyItem, page?: number): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/job-entry`, args, {
      params: { page, size: page ? 100 : MAX_SIZE },
    });
  }

  static async getCandidatesStatisticByVacancyAndStatus(
    args: StatisticBodyItem,
    page: number,
    status: string,
    vacancy?: string,
  ): Promise<AxiosResponse> {
    return $api.post(
      `/recruiting-tool/api/v1/statistics/event/details`,
      {
        ...args,
        status,
        vacancy: vacancy === '0' ? null : vacancy,
        ...(vacancy === '0' ? { statusWithoutVacancy: true } : {}),
      },
      {
        params: { page, size: 24 },
      },
    );
  }

  static async getCandidatesStatisticInRequests(
    args: StatisticBodyItem,
    page: number,
    itemType: string,
    vacancyClaimId?: string,
  ): Promise<AxiosResponse> {
    return $api.post(
      `recruiting-tool/api/v1/statistics/claim/metrics/story/details`,
      { ...args },
      {
        params: { page, size: 24, itemType, vacancyClaimId: vacancyClaimId || '' },
      },
    );
  }

  static async getCandidatesStatisticInRequestsHired(
    args: StatisticBodyItem,
    page: number,
    vacancyClaimId?: string,
  ): Promise<AxiosResponse> {
    return $api.post(
      `recruiting-tool/api/v1/statistics/claim/metrics/hired/details`,
      { ...args },
      {
        params: { page, size: 24, vacancyClaimId: vacancyClaimId || '' },
      },
    );
  }

  static async exportCandidatesInReport(
    args: StatisticBodyItem,
    status: string,
    vacancy?: string,
  ): Promise<AxiosResponse> {
    return $api.post(
      `/recruiting-tool/api/v1/statistics/event/details/export`,
      {
        ...args,
        status,
        vacancy: vacancy === '0' ? null : vacancy,
        ...(vacancy === '0' ? { statusWithoutVacancy: true } : {}),
      },
      {
        responseType: 'json',
      },
    );
  }

  static async exportCandidatesInReportRequests(
    args: StatisticBodyItem,
    itemType: string,
    vacancyClaimId?: string,
  ): Promise<AxiosResponse> {
    return $api.post(
      `recruiting-tool/api/v1/statistics/claim/metrics/story/details/export`,
      { ...args },
      {
        params: {
          itemType,
          vacancyClaimId: vacancyClaimId || '',
        },
        responseType: 'json',
      },
    );
  }

  static async exportCandidatesInReportRequestsHired(
    args: StatisticBodyItem,
    vacancyClaimId?: string,
  ): Promise<AxiosResponse> {
    return $api.post(
      `recruiting-tool/api/v1/statistics/claim/metrics/hired/details/export`,
      { ...args },
      {
        params: { vacancyClaimId: vacancyClaimId || '' },
        responseType: 'json',
      },
    );
  }

  static async getRequestMetrics(args: StatisticBodyItem): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/claim/metrics`, args);
  }

  static async getRequestMetricsForTable(args: StatisticBodyItem, page?: number): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/statistics/claim/metrics/table`, args, {
      params: { page, size: page ? 100 : MAX_SIZE },
    });
  }
}
